import {RootState, SignalType, TelegramSignalsResponse} from '../types'
import {
  CHART_READY,
  FULFILL_LOGIN,
  FULFILL_USER_ACCOUNT,
  INIT_APP,
  RELOGIN_TO_MT4,
  SELECT_ACCOUNT,
  FULFILL_PRICES,
  FULFILL_ASSET_GROUPS,
  FULFILL_BONDS,
} from '../constants'
import { Epic, ActionsObservable } from 'redux-observable'
import { MiddlewareAPI, Action } from 'redux'
import { InitApp, setLocale } from '../actions/commonActions'
import { initGraphQLRepo } from '../actions/GraphQLActions'
import { GraphQLRepository } from '../utils/graphQL-repository'
import { MT4Connector } from '@bdswiss/mt4-connector'
import {
  FulfillLogin,
  /*loginToMt4,*/ FulfillPrices,
  FulfillAssetGroups,
} from '../actions/mt4Actions'
import {getServerTypeByAccountType, getSignalsType} from '../utils/common-utils'
import { CONFIRM_MODAL } from '../constants/modal-const'
import { unsetModal } from '../actions/dialogActions'
import { Observable } from 'rxjs/Observable'
import { SelectAccount, selectAccount } from '../actions'
import { SymbolsStorage } from '../lib/charting_library/datafeed/symbols-storage'
import { AssetGroup, Prices } from '@bdswiss/mt4-connector'
import { FulfillBonds } from '../actions/bondActions'
import {map} from "rxjs/operators";
import {ajax} from "rxjs/observable/dom/ajax";
import {fulfillSimpleTradingSignals, fulfillTelegramSignals} from "../actions/signalsActions";
import {of} from "rxjs/observable/of";

const mt4 = MT4Connector.Instance

export const startAppInitialization: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<InitApp>)
    .ofType(INIT_APP)
    .map(({ accountLogin, authToken, graphQlURL }) => {
      GraphQLRepository.Instance.Url = graphQlURL
      return initGraphQLRepo(authToken, accountLogin)
    })

export const setLocaleOnInit: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  (action$ as ActionsObservable<InitApp>).ofType(INIT_APP).map(({ locale }) => {
    return setLocale(locale!)
  })

export const getSimpleSignalsOnInit: Epic<Action, RootState> = (
    action$,
) =>
    (action$ as ActionsObservable<InitApp>).ofType(INIT_APP).mergeMap((action) => {
        if( getSignalsType() !== SignalType.Simple){
            return []
        }
        return ajax({
            url: action.graphQlURL + '/api/v1/marketing/trading-signals',
            withCredentials: true,
        })
            .catch(()=> {
                return of(null)
            })
            .pipe(
                map(response => {
               return fulfillSimpleTradingSignals(response?.response?.data?.tradingSignals ?? [])
                })
            )
    })

export const getTelegramSignalsOnInit: Epic<Action, RootState> = (
    action$,
) =>
    (action$ as ActionsObservable<InitApp>).ofType(INIT_APP).mergeMap((action) => {
        if( getSignalsType() !== SignalType.Telegram){
            return []
        }
        return ajax({
            url: action.graphQlURL + '/api/v1/marketing/telegram-trading-signals',
            withCredentials: true
        })
            .catch(()=> {
                return of(null)
            })
            .pipe(
            map(response => {
                const empty : TelegramSignalsResponse =  {
                    clientStatistics: {
                        usedSignals: 0,
                        availableSignals:0
                    },
                    signals: []
                }
                return fulfillTelegramSignals(response?.response?.data ?? empty)
            })
        )
    })

export const triggerMt4Login: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(FULFILL_USER_ACCOUNT).mergeMap((action: Action) => {
    const { accountLogin, mt4Version, mt4Platform } = state.getState().appConfig
    const accDetail = state
      .getState()
      .userAccount.viewer.accounts.find(acc => acc.login === accountLogin)!
    const { __typename: accountTypename, login, metaTraderApiUrl } = accDetail
    const server = getServerTypeByAccountType(accountTypename)
    mt4.connect(metaTraderApiUrl, {
      login,
      server,
      version: mt4Version,
      platform: mt4Platform,
    })
    return Observable.empty() // loginToMt4(login, server, authToken) login command is sent by mt4-connector;
  })

export const triggerMt4ReLogin: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(RELOGIN_TO_MT4).map((action: Action) => {
    const { activeAccount } = state.getState().appState
    return selectAccount(activeAccount)
  })

export const notifyParentReady: Epic<any, RootState> = (action$, state: MiddlewareAPI<RootState>) =>
  Observable.zip(action$.ofType(CHART_READY), action$.ofType(INIT_APP), (chartReady, appInited) => {
    window.parent.postMessage('Ready', '*')
  }).switchMap(() => Observable.empty())

export const notifyParentAccountSwitched: Epic<any, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  Observable.zip(
    action$.ofType(FULFILL_LOGIN),
    action$.ofType(SELECT_ACCOUNT),
    (login: FulfillLogin, { id }: SelectAccount) => {
      window.parent.postMessage(
        {
          action: 'account-switched',
          accountId: id,
        },
        '*'
      )
    }
  ).switchMap(() => Observable.empty())

export const triggerConfirmAction: Epic<Action, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(CONFIRM_MODAL).mergeMap(action => {
    if (state.getState().modal) {
      return Observable.of(state.getState().modal!.confirmAction, unsetModal())
    } else {
      return Observable.of(unsetModal())
    }
  })

export const datafeedSymbolsStorageInit: Epic<
  Action | FulfillPrices | FulfillAssetGroups,
  RootState
> = (action$, state: MiddlewareAPI<RootState>) =>
  Observable.zip(
    action$.ofType(FULFILL_PRICES).map(action => (action as FulfillPrices).prices),
    action$.ofType(FULFILL_ASSET_GROUPS).map(action => (action as FulfillAssetGroups).assetGroups),
    (prices: Prices, assetGroups: AssetGroup[]) => {
      SymbolsStorage.Instance._init(prices, assetGroups)
    }
  ).switchMap(() => Observable.empty())

export const datafeedSymbolsStorageInitForBonds: Epic<FulfillBonds, RootState> = (
  action$,
  state: MiddlewareAPI<RootState>
) =>
  action$.ofType(FULFILL_BONDS).switchMap(({ bonds }) => {
    SymbolsStorage.Instance._initFromBonds(bonds)
    return Observable.empty()
  })
